import { Component, EventEmitter, OnDestroy, Output, ViewChild } from '@angular/core';
import { SharedComponentsModule } from '../../../shared/module/shared-components.module';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { defer, firstValueFrom, from, Observable, of, Subject, take } from 'rxjs';
import { AutoScrollDirective } from '../../../../util/directives/auto-scroll.directive';
import { BaseChartComponent } from '../../../base-charts/base-chart/base-chart.component';
import { AiButtonComponent } from '../../../shared/component/buttons/ai-button/ai-button.component';
import { AvatarIconComponent } from '../../../profile/component/avatar-icon/avatar-icon.component';
import { MarkdownComponent } from 'ngx-markdown';
import { CheckBoxSelector, InputProps } from '../../../shared/component/checkbox-selector/checkbox-selector.component';
import { CompanyMiniDto } from '../../../recovery/component/recovery-company/dto/CompanyWithOrgDto';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OrganizationsService } from '../../../organization/components/service/organizations.service';
import { Fund } from '../../../fund-performance/dto/Fund';
import { delay, filter, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { CompanyBackendService } from '../../../pp/service/company-backend.service';
import { SelectorComponent } from '../../../shared/component/input/selector/selector.component';
import { GlobalAiService } from '../../service/global-ai.service';
import {
  AiChart,
  AiChatWithCharts,
  AiHistoryMessage,
  DataSourceType, getDefaultAiRequests, GlobalAiDebugInfoDto,
  GlobalAiRequestDto,
  MessageType,
} from "../../utils";
import { fadeInOutAnimation } from '../../../../util/animations/fade-in-out.animation';
import { PortfolioPerformanceBackendService } from '../../../pp/service/portfolio-performance-backend.service';
import { cloneDeep } from 'lodash';
import { Router } from '@angular/router';
import { FundPerformanceBackendService } from '../../../fund-performance/service/fund-performance-backend.service';
import { CompanyPerformanceService } from '../../../company-performance/service/company-performance.service';
import { HorNavbarService } from "../../../../core/service/hor-navbar.service";
import {
  FundsSelectorDropdownService,
} from "../../../shared/component/funds-selector-dropdown/FundsSelectorDropdown.service";
import { AI_STRINGS } from '../../../ai/utils';
import { DateService } from '../../../../util/common/date-service/date.service';
import { NOTIFICATIONS, NotificationsService } from "../../../../core/service/notifications.service";
import { EnvironmentService } from "../../../../../environments/environment.service";
import {
  TableGroupIconComponent,
} from "../../../shared/component/icons-deprecated/table-group-icon/table-group-icon.component";
import { INVESTORS_URL } from "../../../investors-and-net-irr/dto/InvestorsURL";

@Component({
  selector: 'app-global-ai-chat-modal',
  standalone: true,
  imports: [
    SharedComponentsModule,
    AutoScrollDirective,
    BaseChartComponent,
    AiButtonComponent,
    AvatarIconComponent,
    MarkdownComponent,
    TableGroupIconComponent,
  ],
  templateUrl: './global-ai-chat-modal.component.html',
  styleUrl: './global-ai-chat-modal.component.scss',
  animations: [fadeInOutAnimation()],
})
export class GlobalAiChatModalComponent implements OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();
  @Output() closeEmitter: EventEmitter<void> = new EventEmitter<void>();
  /** ViewChild to the company selector to be able to patch value properly, see {@link fetchDataForCompany} */
  @ViewChild('companySelector') companySelector: SelectorComponent;
  /** ViewChild to the fund selector to be able to patch value properly, see {@link fetchDataForFund} */
  @ViewChild('fundSelector') fundSelector: SelectorComponent;

  /** list of default AI query suggestions based on the selected data source */
  defaultActions: string[];
  /** subject to trigger auto-scrolling reset when new content is added */
  autoScrollReset$ = new Subject<void>();
  /** chat state object containing messages and charts */
  chat = new AiChatWithCharts();
  /** config for the checkbox funds selector */
  checkboxSelectorProps: InputProps = { emptyArrayText: 'No funds yet', title: 'Funds', isSelectAllEnabled: true };
  /** list of periods for periods selector */
  periods: Date[];
  /** map of company names to company objects for the company selector */
  companies: Map<string, CompanyMiniDto>;
  /** map of fund names to fund objects for the funds selector */
  funds: Map<string, Fund>;

  selectedFundsCount: number;

  formGroup: GlobalAiFormGroup;
  dataSources = Object.values(DataSourceType);
  protected readonly DataSourceType = DataSourceType;
  protected readonly MessageType = MessageType;
  protected readonly AiStrings = AI_STRINGS;

  isDebugExpanded = false;
  /** Debugging chunks for display in show details section */
  debuggingChunks: GlobalAiDebugInfoDto;

  /** debug is restricted for prod (check is done on backend as well)*/
  isProd: boolean;

  constructor(
    private hornavbarService: HorNavbarService,
    private orgService: OrganizationsService,
    private companyBackendService: CompanyBackendService,
    private ppBackendService: PortfolioPerformanceBackendService,
    private fpBackendService: FundPerformanceBackendService,
    private router: Router,
    private cpService: CompanyPerformanceService,
    private fundsSelectorDropdownService: FundsSelectorDropdownService,
    private notificationsService: NotificationsService,
    private envService: EnvironmentService,
    protected globalAiService: GlobalAiService,
  ) {
    this.globalAiService.fetchConversationHistory(this.orgService.selected$.value.id);
    this.initFormAndFormSubs();
    this.initChatSubs();
    this.isProd = this.envService.isProduction();
  }

  ngOnDestroy(): void {
    this.globalAiService.clearObservables();
    this.globalAiService.cancelActiveRequest();
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  sendRequest(userInput: string): void {
    if (this.formGroup.controls.dataSource.value !== DataSourceType.ORGANIZATION && !this.periods?.length) {
      this.notificationsService.warning(NOTIFICATIONS.INSUFFICIENT_DATA);
      return;
    }
    this.formGroup.controls.requestInput.setValue(userInput);
    this.globalAiService.sendStreamingRequestWithCharts(this.getRequestDto());
    this.formGroup.controls.requestInput.setValue('');
  }

  closeModal(): void {
    this.closeEmitter.emit();
  }

  async clearChat(): Promise<void> {
    await firstValueFrom(this.globalAiService.clearChat(this.orgService.selected$.value.id));
  }

  async toggleDebugDetails(): Promise<void> {
    if (this.isProd) {
      return;
    }
    this.isDebugExpanded = !this.isDebugExpanded;

    this.debuggingChunks = this.isDebugExpanded
      ? await firstValueFrom(this.globalAiService.getDebugInfo(this.getRequestDto()))
      : undefined;
  }

  async onFundsSelectionChange(): Promise<void> {
    this.countSelectedFunds();
    await this.fetchPeriodsForCompany();
  }

  /**
   * Fetches available periods for the selected company and funds.
   *
   * This method:
   * 1. Checks if the current data source is Company (returns early if not)
   * 2. Gets the UUIDs of all selected funds
   * 3. Handles the case when no funds are selected by clearing periods
   * 4. Fetches periods that are visible for the selected funds (meaning that the {@link FundPerformance#isVisible} is true for the fund+period)
   * 5. Updates the selected period based on availability {@link updateSelectedPeriodAfterPeriodsFetch}
   *
   * This is called when the user selects/unselects fund in Company data source mode
   * or when the data source was changed to Company.
   */
  async fetchPeriodsForCompany(): Promise<void> {
    if (this.formGroup.controls.dataSource.value !== DataSourceType.COMPANY) {
      return;
    }
    const selectedFundsUuids = this.formGroup.controls.funds.value
      .filter((fund) => fund.chosen)
      .map((fund) => fund.UUID);

    if (!selectedFundsUuids.length) {
      this.periods = [];
      this.formGroup.controls.period.patchValue(undefined);
      return;
    }

    this.periods = await firstValueFrom(this.fpBackendService.getAllVisiblePeriodsForFunds(selectedFundsUuids));
    this.updateSelectedPeriodAfterPeriodsFetch();
  }

  /**
   * Initializes the form controls and sets up subscriptions to form value changes.
   *
   * This method:
   * 1. Determines the initial data source based on the current URL
   * 2. Creates form controls for all inputs (request, data source, funds, etc.)
   * 3. Sets up subscriptions to handle interdependencies between selections:
   *    - Data source changes trigger fetching appropriate selector data
   *    - Company selection triggers fetching associated funds
   *    - Fund selection triggers fetching available periods
   *
   * These subscriptions ensure that when a user changes one selection,
   * all dependent selections are updated accordingly.
   */
  private initFormAndFormSubs(): void {
    const initialDataSource = this.getDataSourceFromUrl();
    this.formGroup = new FormGroup({
      requestInput: new FormControl<string>('', Validators.required),
      dataSource: new FormControl<DataSourceType>(initialDataSource),
      funds: new FormControl<CheckBoxSelector[]>([]),
      fund: new FormControl<Fund>(null),
      company: new FormControl<CompanyMiniDto>(null),
      period: new FormControl<Date>(null),
    });

    this.formGroup.controls.dataSource.valueChanges
      .pipe(
        startWith(initialDataSource),
        takeUntilDestroyed(),
      ).subscribe((dataSource: DataSourceType): void => {
        this.fetchSelectorsDataByDataSource(dataSource);
      });

    this.formGroup.controls.company.valueChanges
      .pipe(
        switchMap((company: CompanyMiniDto) =>
          this.ppBackendService.getFundsIdsByCompanyId(company.id)
        ),
        map((fundsUuids: string[]): CheckBoxSelector[] => {
          const allFunds = cloneDeep(this.orgService.selected$.value.funds);
          const companyFunds = allFunds.filter((fund: Fund) => fundsUuids.includes(fund.uuid));
          const funds = this.mapFundsToCheckboxSelector(companyFunds);
          funds.sort((a, b) => a.item.localeCompare(b.item));
          return funds;
        }),
        takeUntilDestroyed()
      )
      .subscribe((funds: CheckBoxSelector[]): void => {
        this.formGroup.controls.funds.patchValue(funds);
      });

    this.formGroup.controls.fund.valueChanges
      .pipe(
        switchMap((fund: Fund) => this.fpBackendService.getAllVisiblePeriods(fund.uuid)),
        takeUntilDestroyed(),
      ).subscribe((periods) => {
        this.periods = periods;
        this.updateSelectedPeriodAfterPeriodsFetch();
      });

    this.formGroup.controls.funds.valueChanges
      .pipe(
        startWith(this.formGroup.controls.funds),
        takeUntilDestroyed()
      )
      .subscribe(() => this.countSelectedFunds());
  }

  private fetchSelectorsDataByDataSource(dataSource: DataSourceType): void {
    this.defaultActions = getDefaultAiRequests(dataSource);
    switch (dataSource) {
      case DataSourceType.ORGANIZATION:
        this.fetchDataForOrg();
        return;
      case DataSourceType.COMPANY:
        this.fetchDataForCompany();
        return;
      case DataSourceType.FUND:
        this.fetchDataForFund();
    }
  }

  /**
   * Maps fund objects to checkbox selector items, preserving selection state.
   *
   * If a user has previously selected/unselected funds in {@link OrganizationOverviewComponent},
   * those selections are preserved in this component's fund selector.
   */
  private mapFundsToCheckboxSelector(funds: Fund[]): CheckBoxSelector[] {
    const fundsFromOrgOverview = this.fundsSelectorDropdownService.fundsData.value;
    return funds.map((fund: Fund) => {
      // find the corresponding fund performance overview
      const fundOverview = fundsFromOrgOverview.all.find((f) => f.fundUuid === fund.uuid);
      const isChosen = fundOverview ? fundOverview.isShown : true;
      return { item: fund.name, itemId: fund.id, UUID: fund.uuid, chosen: isChosen } as CheckBoxSelector;
    });
  }

  /**
   * When the data source is set to Organization, this method is called to
   * populate the funds selector with all funds in the organization.
   */
  private fetchDataForOrg(): void {
    const funds = this.mapFundsToCheckboxSelector(this.orgService.selected$.value.funds);
    funds.sort((a, b) => a.item.localeCompare(b.item));
    this.formGroup.controls.funds.patchValue(funds);
    this.formGroup.controls.period.patchValue(DateService.toHyphenDate(new Date()) as unknown as Date);
  }

  /**
   * This method:
   * 1. Maps all funds in the organization to a Map structure for the selector
   * 2. Determines which fund to select by default:
   *    - Uses the currently selected fund in the organization if available
   *    - Falls back to the first fund in the list if no fund is selected
   * 3. "Manually" elects the fund in the selector component
   *
   * Note: There are some limitations with the selector component that require special handling:
   * - Selectors work best with simple data types, not complex objects
   * - A Map structure is used to work around this limitation
   * - Direct form control updates don't trigger selection, so the selector's method is called directly via {@link fundSelector}
   */
  private fetchDataForFund(): void {
    defer((): Observable<Fund[]> => {
      this.mapFundsToSelectorData();
      return of(Array.from(this.funds.values()));
    }).pipe(
      filter((fundsArray: Fund[]): boolean => fundsArray.length > 0),
      map((fundsArray: Fund[]): string => {
        // check if there's a selected fund in the organization
        const selectedFund = this.orgService.selected$.value.selectedFund;
        if (selectedFund && this.funds.has(selectedFund.name)) {
          // if the selected fund exists in our funds map, select it
          return selectedFund.name;
        }
        // otherwise use the first fund in the list
        return fundsArray[0].name;
      }),
      delay(5), // delay is needed to find a ViewChild and call its function
      tap((fundNameToSelect: string): void => {
        // selectors are best suited for simple data arrays, like array of strings/enums/numbers
        // they do not work if we simply provide an array of objects, like an array of funds or companies
        // therefore we provide a map of <object.name, object>, which is supported by the selectors
        // however, simply pathing the value in form control does not work (clicking on it works as expected)
        // so to imitate item selection, we need to call selector's method
        // it is done only on selector creation, s.t. the selector has a default value
        // need to consider changing the selectors probably, but its currently out of the scope of this task
        this.fundSelector.onItemSelect(fundNameToSelect);
      }),
      takeUntil(this._destroyStream)
    ).subscribe();
  }

  /**
   * This method has two main parts:
   *
   * 1. Company data fetching and selection:
   *    - Fetches companies for the current organization
   *    - Maps companies to a format suitable for the selector
   *    - Determines which company to select by default:
   *      - Uses the currently selected company from the company performance service if available
   *      - Falls back to the first company in the list if no company is selected
   *    - "Manually" selects the company in the selector component
   *
   * 2. Period fetching after company selection:
   *    - Sets up a chain of subscriptions to handle the async flow:
   *      a. Wait for the company value to change (after selection)
   *      b. Then wait for the funds to be fetched and updated (triggered by company selection)
   *      c. Finally fetch the periods available for the selected company and funds
   *
   * This complex flow (hopefully, but what do i know) ensures that all dependent data is loaded in the correct sequence,
   * with each step waiting for the previous step to complete before proceeding.
   */
  private fetchDataForCompany(): void {
    // todo artem: ask whether we need to get only those companies which have performances
    from(this.companyBackendService.getMini(this.orgService.selected$.value.uuid)).pipe(
      tap((companiesArray: CompanyMiniDto[]): void => {
        this.companies = new Map(companiesArray.map((company: CompanyMiniDto): [string, CompanyMiniDto] => [company.name, company]));
      }),
      // todo: fallback if no companies ?
      filter((companiesArray: CompanyMiniDto[]): boolean => companiesArray.length > 0),
      map((companiesArray: CompanyMiniDto[]): string => {
        // check if there's a selected company cp service
        const selectedCompany = this.cpService.getSelectedCompany();
        if (selectedCompany) {
          // try finding the company in the companies array that matches the selected company
          const matchingCompany = companiesArray.find((company) => company.id === selectedCompany.companyId);
          if (matchingCompany) {
            return matchingCompany.name;
          }
        }
        // otherwise use the first fund in the list
        return companiesArray[0].name;
      }),
      delay(5), // delay is needed to find a viewChild and call its function
      tap((companyNameToSelect: string): void => {
        // selectors are best suited for simple data arrays, like array of strings/enums/numbers
        // they do not work if we simply provide an array of objects, like an array of funds or companies
        // therefore we provide a map of <object.name, object>, which is supported by the selectors
        // however, simply pathing the value in form control does not work (clicking on it works as expected)
        // so to imitate item selection, we need to call selector's method
        // it is done only on selector creation, s.t. the selector has a default value
        // need to consider changing the selectors probably, but its currently out of the scope of this task
        this.companySelector.onItemSelect(companyNameToSelect);
      }),
      takeUntil(this._destroyStream)
    ).subscribe();

    // we have to wait for the company value to change and then fetch the periods,
    // as soon as company value changes sub functionality is done, we will have the funds fetched,
    // while funds are needed to fetch periods.
    this.formGroup.controls.company.valueChanges.pipe(
      // take only the first emit after the company selector has been updated
      take(1),
      // wait for the funds to be fetched and updated in the form
      switchMap(() => this.formGroup.controls.funds.valueChanges.pipe(
        take(1),
        takeUntil(this._destroyStream)
      )),
      // now fetch the periods
      switchMap(() => {
        return this.fetchPeriodsForCompany();
      }),
      takeUntil(this._destroyStream),
    ).subscribe();
  }

  private getRequestDto(): GlobalAiRequestDto {
    const formData = this.formGroup.getRawValue();
    return {
      request: formData.requestInput,
      dataSourceType: formData.dataSource,
      fundsUuids: formData.funds.filter((f) => f.chosen).map((f) => f.UUID),
      companyUuid: formData.company?.uuid,
      companyId: formData.company?.id,
      period: formData.period,
      orgUuid: this.orgService.selected$.value.uuid,
      orgId: this.orgService.selected$.value.id,
      fundId: formData.fund?.id,
    };
  }

  private initChatSubs(): void {
    this.globalAiService.messages$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((messages: AiHistoryMessage[]): void => {
        this.chat.messages = messages;
      });

    this.globalAiService.charts$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((charts: AiChart[]): void => {
        this.chat.charts = charts;
      });
  }

  private mapFundsToSelectorData(): void {
    this.funds = new Map(this.orgService.selected$.value.funds.map((fund) => [fund.name, fund]));
  }

  /**
   * Determines the appropriate data source based on the current URL.
   *
   * For example:
   * - If the URL contains "organization", it returns {@link DataSourceType.ORGANIZATION}
   * - If the URL contains "company", it returns {@link DataSourceType.COMPANY}
   * - If the URL contains "fund" or "investors", it returns {@link DataSourceType.FUND}
   *
   * If no match is found, it defaults to DataSourceType.ORGANIZATION.
   */
  private getDataSourceFromUrl(): DataSourceType {
    const url = this.router.url.toLowerCase();

    if (url.includes(INVESTORS_URL.BASE)) {
      return DataSourceType.FUND;
    }

    const found = (Object.values(DataSourceType) as string[])
      .find((ds) => url.includes(ds.toLowerCase()));
    return (found as DataSourceType) ?? DataSourceType.ORGANIZATION;
  }

  /**
   * Updates the selected period in the form control after the list of available periods has been fetched
   * by following this selection logic:
   *
   * 1. If the current period value in the form is valid (exists and is in the periods list), keep it unchanged
   * 2. Otherwise, use the selected period from hornavbarService if it's present in the available periods
   * 3. If neither of the above conditions is met, default to the first period in the list
   */
  private updateSelectedPeriodAfterPeriodsFetch(): void {
    const currentPeriod = this.formGroup.controls.period.value;
    // in case period value exists and is valid, keep it and return
    if (currentPeriod && this.periods.includes(currentPeriod)) {
      return;
    }

    // otherwise: use selectedPeriod from hornavbar if its present in the list, or default to the first period in the list
    const newPeriod = this.periods.includes(this.hornavbarService.selectedPeriod)
      ? this.hornavbarService.selectedPeriod
      : this.periods.at(0);

    this.formGroup.controls.period.patchValue(newPeriod);
  }

  private countSelectedFunds(): void {
    this.selectedFundsCount = this.formGroup.controls.funds.value
      .filter((fund) => fund.chosen).length;
  }
}

type GlobalAiFormGroup = FormGroup<{
  requestInput: FormControl<string>;
  dataSource: FormControl<DataSourceType>;
  fund: FormControl<Fund>;
  funds: FormControl<CheckBoxSelector[]>;
  company: FormControl<CompanyMiniDto>;
  period: FormControl<Date>;
}>;
